import React, { useEffect, useState } from 'react';
import './Summary.css'
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GetElsLookupsState } from '../../redux/reducers/getLookups/getElsLookupsReducer';
import { getSummaryDataState } from '../../redux/reducers/getSummaryData/summaryDataReducer';
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion'
import AccordionItem, { DefaultAccordionProviderOptions } from '../../components/common/AccordionItem/AccordionItem'
import { commaSeparate, formatCurrency } from '../../utils/formatter';
import { ProfileOverviewState } from '../../redux/reducers/getProfile/getProfileOverviewReducer';
import { selectELSLookups, selectProfileGetOverview, selectSummaryData } from '../../redux/selectors/exportSelectorPaths';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';

interface SuiteSummaryProps {
    unitType?: "4" | "5" | "6";
}

export default function SuiteSummary(props: SuiteSummaryProps): JSX.Element {
    const { t, i18n } = useTranslation(['guided_wizard', 'navigation', 'parking_property', 'summary']);
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const profileTypeCode = profileOverview.profile.profile_type_code.toLowerCase()
    const summaryData: getSummaryDataState = useSelector(selectSummaryData);
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const [suiteCounts, setSuiteCounts] = useState<Record<string, number>>({});
    const currencySymbol = "$";
    const propertyType: "multires" | "landLease" | "unitSubType4" | undefined = props.unitType ? "unitSubType4" : (sessionStorage.getItem("propertyCode") ? (["307", "382"].includes(sessionStorage.getItem("propertyCode") as string) ? "landLease" : "multires") : undefined)
    const multiresUnitSubtype = elsLookupsState.dropdowns && (elsLookupsState.dropdowns["multiresSubType"] as Record<string, unknown>[]);
    const multiresRowHouseUnitSubtype = elsLookupsState.dropdowns && (elsLookupsState.dropdowns["multiresRowHouseSubType"] as Record<string, unknown>[]);
    const landLeaseUnitSubtype = elsLookupsState.dropdowns && (elsLookupsState.dropdowns["landLeaseSubType"] as Record<string, unknown>[]);
    const unitSubType4 = elsLookupsState.dropdowns && (elsLookupsState.dropdowns["unitSubType4"] as Record<string, unknown>[]);
    const lookup = props.unitType ? (unitSubType4 ?? []) : (propertyType === "landLease" ? (landLeaseUnitSubtype ?? []) : [...(multiresUnitSubtype ?? []), ...(multiresRowHouseUnitSubtype ?? [])])
    const simplifiedLookup = lookup.reduce((simplifiedLookup, unitSubtype) => ({ [unitSubtype["unitSubtypeCode"] as string]: unitSubtype["unitSubtypeDescription"] as string, ...simplifiedLookup }), { "undefined": "" });
    const [totalUnitCount, setTotalUnitCount] = useState(0);
    const grouping = {
        "301": "1 Bed",
        "302": "2 Bed",
        "303": "3 Bed",
        "304": "4 Bed and up",
        "305": "4 Bed and up",
        "306": "4 Bed and up",
        "307": "4 Bed and up",
        "308": "Bachelor / Bachelorette",
        "309": "Bachelor / Bachelorette",
        "310": "1 Bed",
        "311": "2 Bed",
        "312": "3 Bed",
        "313": "4 Bed and up",
        "314": "4 Bed and up",
        "315": "Bachelor / Bachelorette",
        "317": "1 Bed",
        "318": "2 Bed",
        "319": "3 Bed",
        "320": "4 Bed and up",
        "321": "4 Bed and up",
        "322": "4 Bed and up",
        "323": "4 Bed and up",
        "324": "Bachelor / Bachelorette",
        "327": "1 Bed",
        "328": "2 Bed",
        "329": "3 Bed",
        "330": "4 Bed and up",
        "331": "4 Bed and up",
        "332": "4 Bed and up",
        "333": "4 Bed and up",
        "334": "Bachelor / Bachelorette",
        "343": "Studio / Room",
        "344": "1 Bed",
        "345": "2 Bed",
        "346": "3 Bed",
        "347": "4 Bed and up",
        "348": "4 Bed and up",
        "349": "4 Bed and up",
        "350": "4 Bed and up",
        "351": "Bachelor / Bachelorette",
        "352": "Studio / Room"
    }
    const unitSubType3 = [
        { unitSubtypeDescription: "Bachelor / Bachelorette", unitSubtypePosition: 0 },
        { unitSubtypeDescription: "1 Bed", unitSubtypePosition: 1 },
        { unitSubtypeDescription: "2 Bed", unitSubtypePosition: 2 },
        { unitSubtypeDescription: "3 Bed", unitSubtypePosition: 3 },
        { unitSubtypeDescription: "4 Bed and up", unitSubtypePosition: 4 }
    ]
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const formatRent = (value: number) => currencySymbol + formatCurrency(value, i18n.language)

    useEffect(() => {
        const stateToUse = (props.unitType ? (summaryData.data as Record<string, unknown>)[props.unitType] : (summaryData.data as Record<string, unknown>)["apartment_summary"])
        const suiteNumber: Record<string, number> = { "minRent": -1, "maxRent": -1 };
        let unitCount = 0;
        stateToUse && (stateToUse as Record<string, unknown>[]).forEach((suite) => {
            const groupKey = ((suite["unitSubtypeCode"] as string) in grouping) ? grouping[suite["unitSubtypeCode"] as keyof typeof grouping] : simplifiedLookup[suite["unitSubtypeCode"] as keyof typeof simplifiedLookup] as string;
            if (!(groupKey in suiteNumber)) {
                suiteNumber[groupKey] = 0;
                suiteNumber[groupKey + "MinRent"] = -1;
                suiteNumber[groupKey + "MaxRent"] = -1;
            }
            if (["bip", "htl", "mtl", "ltc", "lle", "glf"].includes(profileTypeCode)) {
                if ("unitCount" in suite && suite["unitCount"] && (suite["unitCount"] as number) > 0) {
                    const tenantUnitCount = Number(suite["unitCount"]);
                    suiteNumber[groupKey] = suiteNumber[groupKey] + tenantUnitCount;
                    unitCount += tenantUnitCount;
                } else {
                    suiteNumber[groupKey] = suiteNumber[groupKey] + 1;
                    unitCount += 1;
                }
            }
            if (["rho"].includes(profileTypeCode)) {
                if ("tenantUnitNumber" in suite && suite["tenantUnitNumber"] && (suite["tenantUnitNumber"] as number) > 0) {
                    const tenantUnitCount = Number(suite["tenantUnitNumber"]);
                    suiteNumber[groupKey] = suiteNumber[groupKey] + tenantUnitCount;
                    unitCount += tenantUnitCount;
                } else {
                    suiteNumber[groupKey] = suiteNumber[groupKey] + 1;
                    unitCount += 1;
                }
            }
            if (suite["monthlyMarketRent"]) {
                if ((suite["monthlyMarketRent"] as number) < suiteNumber[groupKey + "MinRent"] || suiteNumber[groupKey + "MinRent"] === -1) {
                    suiteNumber[groupKey + "MinRent"] = (suite["monthlyMarketRent"] as number);
                    if ((suite["monthlyMarketRent"] as number) < suiteNumber["minRent"] || suiteNumber["minRent"] === -1) {
                        suiteNumber["minRent"] = suite["monthlyMarketRent"] as number;
                    }
                }
                if ((suite["monthlyMarketRent"] as number) > suiteNumber[groupKey + "MaxRent"]) {
                    suiteNumber[groupKey + "MaxRent"] = (suite["monthlyMarketRent"] as number);
                    if ((suite["monthlyMarketRent"] as number) > suiteNumber["maxRent"]) {
                        suiteNumber["maxRent"] = suite["monthlyMarketRent"] as number;
                    }
                }
            }
        });
        setTotalUnitCount(unitCount);
        setSuiteCounts(suiteNumber);
        // eslint-disable-next-line
    }, [summaryData.data, props.unitType])

    const sortSuites = (suites: string[]) => {
        let orderedSuites: { description: string, position: number }[] = []
        const dropdown = props.unitType ? unitSubType4 : unitSubType3
        for (const i in suites) {
            const index = dropdown.findIndex(e => e.unitSubtypeDescription === suites[i])
            orderedSuites = [...orderedSuites, { description: suites[i], position: index }]
        }
        return orderedSuites.sort((a, b) => a.position - b.position).map(e => e.description)
    }

    const buildSuiteSummary = (): JSX.Element[] => {
        const suiteSummary: JSX.Element[] = [];
        suiteSummary.push(<div className='summary-section-container' key='suite-summary-unit-count'>
            <table className='summary-table' role='presentation'>
                <thead>
                    <tr>
                        <th>{t("unit-heading.unit-count", { ns: "guided_wizard" })}</th>
                        <td>{commaSeparate(totalUnitCount)}</td>
                    </tr>
                </thead>
                <tbody>
                    {sortSuites(Object.keys(suiteCounts).filter((key) => key.substring(key.length - 4) !== "Rent")).map((key, index) => (
                        <tr key={`suite-row-unit-count-${index}`}>
                            <td>
                                {key === "undefined" ? "" : key}
                            </td>
                            <td>
                                {commaSeparate(suiteCounts[key])}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>);
        suiteSummary.push(<div className='summary-section-container' key='suite-summary-rent-range'>
            <table className='summary-table' role='presentation'>
                <thead>
                    <tr>
                        <th>{t("unit-heading.monthly-rent-range", { ns: "guided_wizard" })}</th>
                        <td>{suiteCounts["minRent"] === suiteCounts["maxRent"] ? (suiteCounts["minRent"] === -1 ? "" : formatRent(suiteCounts["minRent"])) : (formatRent(suiteCounts["minRent"]) + " - " + formatRent(suiteCounts["maxRent"]))}</td>
                    </tr>
                </thead>
                <tbody>
                    {sortSuites(Object.keys(suiteCounts).filter((key) => key.substring(key.length - 4) !== "Rent")).map((key, index) => (
                        <tr key={`suite-row-rent-range-${index}`}>
                            <td>
                                {key === "undefined" ? "" : key}
                            </td>
                            <td>
                                {suiteCounts[key + "MinRent"] === suiteCounts[key + "MaxRent"] ? (suiteCounts[key + "MinRent"] === -1 ? "" : formatRent(suiteCounts[key + "MinRent"])) : (formatRent(suiteCounts[key + "MinRent"]) + " - " + formatRent(suiteCounts[key + "MaxRent"]))}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>);

        return suiteSummary;
    }

    return (
        <>
            <LoadingSpinner loading={elsLookupsState.loading}>
                {Object.keys(suiteCounts).length > 0 &&
                    <ControlledAccordion providerValue={providerValue}>
                        <AccordionItem header={props.unitType === "4" ? t("independentLiving", { ns: "navigation" }) :
                            (props.unitType === "5" ? t("assistedLiving", { ns: "navigation" }) :
                                (props.unitType === "6" ? t("memoryCare", { ns: "navigation" }) : t("apartment", { ns: "summary" })))}
                            key="suiteSummary"
                            itemKey="suiteSummary"
                            headingLevel={2}>
                            <div className='summary-grid'>{buildSuiteSummary()}</div>
                        </AccordionItem>
                    </ControlledAccordion>
                }
            </LoadingSpinner>
        </>
    )
}